import { useEffect, useId, useMemo, useRef, useState } from "react";
import { AlertCircle, AlertTriangle, Info, Trash2 } from "lucide-react";

import type { SchemaField } from "../../components/SchemaEditor/SchemaEditor";
import { isWholeNumber, prettyJson } from "../../lib/schemaFields";
import {
  compactJson,
  isEditableScalar,
  metadataEqual,
  sameJsonValue,
  valueKind,
  type MetadataValueKind,
} from "./metadata";
import styles from "./GuildMetadataEditor.module.css";

interface GuildMetadataEditorProps {
  /** Metadata schema from games.config.guild.metadata_schema. Empty → raw-only. */
  schema: SchemaField[];
  /**
   * `guild.recommendations.requirement_guild_key` — the schema key the guild
   * recommendation feed gates on. Empty when the project set none; the key it
   * names wears a `requirement` tag so an operator can see they are moving the
   * threshold, not just a number.
   */
  requirementKey: string;
  /**
   * The guild's metadata as loaded. It seeds the editor once: like
   * PlayerStatsEditor, this component owns its state after mount and is
   * uncontrolled — the parent resets it by remounting with a changing React
   * `key` (Discard, or a refetch after a save), never by feeding a new `value`.
   */
  value: Record<string, unknown>;
  /** Called with the next whole metadata object on every change. */
  onChange: (next: Record<string, unknown>) => void;
  /** Called with the editor's validity (false blocks the page Save). */
  onValidityChange: (valid: boolean) => void;
}

type Mode = "form" | "raw";

/**
 * One key the schema does not declare. `text` is the editing state of a scalar;
 * `raw` is the untouched value of everything else, which the form shows but
 * never rewrites.
 */
interface ExtraEntry {
  key: string;
  kind: MetadataValueKind;
  text: string;
  raw: unknown;
}

/**
 * A metadata value as the string its input shows. An absent key is an empty
 * string, and an empty string is what "this key is not in the object" means
 * throughout the form — see buildObject.
 *
 * A non-scalar under a schema key (a schema narrowed after the game wrote
 * something structured) is rendered as JSON rather than through String(),
 * which would turn it into "[object Object]" and lose it on the next save.
 */
function toFieldString(raw: unknown): string {
  if (raw === undefined || raw === null) return "";
  if (typeof raw === "boolean") return raw ? "true" : "false";
  if (typeof raw === "object") return compactJson(raw);
  return String(raw);
}

/**
 * The schema in drawing order: the half-width fields first, then the full-width
 * ones (`string`, which takes both columns).
 *
 * The mockup lays a schema of [int, string, bool] out as int | bool over the
 * full-width string, and it is right to: a two-column grid with a wide field in
 * the middle leaves a hole beside it. Sorting here rather than letting CSS
 * (`grid-auto-flow: dense`) backfill keeps the tab order the reading order.
 * Stable, so fields of the same width keep their schema positions.
 */
function drawingOrder(schema: SchemaField[]): SchemaField[] {
  return [...schema].sort(
    (a, b) => Number(a.type === "string") - Number(b.type === "string"),
  );
}

/**
 * Assign a key that came out of JSON.parse.
 *
 * Plain `obj[key] = v` is not safe for one of them: `__proto__` hits the
 * inherited setter instead of creating an own property, so a guild carrying
 * that key would have it dropped by every save — the exact loss this card
 * exists to prevent, for one unlucky name. jsonb stores it happily and the
 * game's own PATCH writes the column freely, so it is reachable.
 */
function setKey(obj: object, key: string, v: unknown): void {
  Object.defineProperty(obj, key, {
    value: v,
    enumerable: true,
    writable: true,
    configurable: true,
  });
}

/** "This field says nothing, so the object carries no such key." */
const ABSENT = Symbol("absent");

/**
 * The value one schema field contributes, given the text it shows.
 *
 * `seeded` is whether the loaded object had this key at all, and it decides
 * one case: a `bool` the guild never had, left switched off, stays absent. The
 * toggle has no third position, so without this the operator who flicks a
 * switch on and back off adds `recruiting: false` to a guild that had no such
 * key — an edit nobody made, and one the off toggle does not show.
 */
function coerce(
  field: SchemaField,
  s: string,
  seeded: boolean,
): unknown | typeof ABSENT {
  if (s === "") return ABSENT;
  if (field.type === "int") {
    // An int that is not a whole number round-trips as the raw string, so the
    // invalid edit stays visible instead of being silently coerced.
    return isWholeNumber(s) ? Number(s.trim()) : s;
  }
  if (field.type === "bool") {
    if (s === "false" && !seeded) return ABSENT;
    return s === "true";
  }
  return s;
}

/**
 * What the form knows about the object it was seeded from, and cannot work out
 * again from its own editing state.
 */
interface Seed {
  /** Schema keys the loaded object actually carried. */
  present: Set<string>;
  /**
   * Keys whose stored value this form cannot say: `null` under an `int`, an
   * object under a `string`, `"yes"` under a `bool`, an empty string anywhere.
   * Each maps the text the field shows to the value that must go back if the
   * operator never touches it.
   */
  unspeakable: Map<string, { text: string; raw: unknown }>;
}

/** Read a Seed off the object the form is (re)seeded from. */
function readSeed(schema: SchemaField[], value: Record<string, unknown>): Seed {
  const present = new Set<string>();
  const unspeakable = new Map<string, { text: string; raw: unknown }>();
  for (const f of schema) {
    if (!Object.hasOwn(value, f.key)) continue;
    present.add(f.key);
    const text = toFieldString(value[f.key]);
    const rebuilt = coerce(f, text, true);
    if (rebuilt === ABSENT || !sameJsonValue(rebuilt, value[f.key])) {
      unspeakable.set(f.key, { text, raw: value[f.key] });
    }
  }
  return { present, unspeakable };
}

/** The editing text of every schema field, seeded from `value`. */
function readFields(
  schema: SchemaField[],
  value: Record<string, unknown>,
): Record<string, string> {
  const init: Record<string, string> = {};
  for (const f of schema) setKey(init, f.key, toFieldString(value[f.key]));
  return init as Record<string, string>;
}

/** The extra keys of `value`, in the order the object carries them. */
function readExtras(
  schema: SchemaField[],
  value: Record<string, unknown>,
): ExtraEntry[] {
  const declared = new Set(schema.map((f) => f.key));
  return Object.keys(value)
    .filter((k) => !declared.has(k))
    .map((k) => {
      const raw = value[k];
      return {
        key: k,
        kind: valueKind(raw),
        text: isEditableScalar(raw) ? String(raw) : "",
        raw,
      };
    });
}

/** What one extra key contributes to the saved object. */
function extraValue(e: ExtraEntry): unknown {
  switch (e.kind) {
    case "bool":
      return e.text === "true";
    case "int":
    case "number": {
      const t = e.text.trim();
      const n = Number(t);
      // A number typed into something that is not one round-trips as the
      // string the operator sees, and the review dialog shows the type change
      // rather than the form deciding it quietly.
      return t !== "" && Number.isFinite(n) ? n : e.text;
    }
    case "string":
      return e.text;
    default:
      return e.raw;
  }
}

/**
 * Rebuild the whole metadata object from the editing state: the schema fields
 * first, then every key the schema never declared.
 *
 * The second half is the difference from PlayerStatsEditor, which rebuilds
 * from the schema alone. Here the object REPLACES the column, so a form that
 * emitted only its own fields would delete `bots_disabled` the first time
 * anybody pressed Save.
 *
 * An empty schema field is an absent key, not an empty value: the form does
 * not invent `min_level: ""` for a guild that never had one, and clearing a
 * field is how the form removes a key it declares.
 *
 * The seed is what keeps that rule from eating data. A stored value the form
 * cannot say — `null` under an `int`, `{…}` under a `string`, `"yes"` under a
 * `bool` — goes back verbatim for as long as its field reads exactly as it was
 * seeded. The card promises the operator that a value it cannot show is "shown
 * as it is rather than blanked"; a save that blanked it anyway would be the
 * card breaking its own word, and on data somebody else wrote.
 */
function buildObject(
  schema: SchemaField[],
  fields: Record<string, string>,
  extras: ExtraEntry[],
  seed: Seed,
): Record<string, unknown> {
  const obj: Record<string, unknown> = {};
  for (const f of schema) {
    const s = fields[f.key] ?? "";
    const kept = seed.unspeakable.get(f.key);
    if (kept && kept.text === s) {
      setKey(obj, f.key, kept.raw);
      continue;
    }
    const v = coerce(f, s, seed.present.has(f.key));
    if (v === ABSENT) continue;
    setKey(obj, f.key, v);
  }
  for (const e of extras) setKey(obj, e.key, extraValue(e));
  return obj;
}

/**
 * GuildMetadataEditor — the Metadata card of the guild page. Ports the t-88
 * mockup `guild-metadata.html`: §1 form mode with the "Outside the schema"
 * block, §3 Raw JSON, and §4's four states (no schema, empty metadata, an
 * invalid `int`, and a stored value whose type the schema disagrees with).
 *
 * It sits inside GuildSettingsForm, which owns the page's single save bar and
 * the save-review dialog; this component only reports what it holds and
 * whether that is valid.
 */
export function GuildMetadataEditor({
  schema,
  requirementKey,
  value,
  onChange,
  onValidityChange,
}: GuildMetadataEditorProps) {
  const hasSchema = schema.length > 0;
  const idPrefix = useId();

  // No schema → there is no typed form to build, so raw is the only mode and
  // the Form/Raw switch is not drawn at all.
  const [mode, setMode] = useState<Mode>(hasSchema ? "form" : "raw");

  const [fields, setFields] = useState<Record<string, string>>(() =>
    readFields(schema, value),
  );
  const [extras, setExtras] = useState<ExtraEntry[]>(() =>
    readExtras(schema, value),
  );
  // What the object the form was seeded from said, for the questions the
  // editing state cannot answer on its own — see buildObject.
  const [seed, setSeed] = useState<Seed>(() => readSeed(schema, value));
  // Whether the operator has changed anything here at all. It gates both the
  // validity report above and every "modified" mark below.
  const [touched, setTouched] = useState(false);
  const [rawText, setRawText] = useState<string>(() => prettyJson(value));

  // Keep `onValidityChange` referentially stable for the effect below: a parent
  // that rebuilds the callback every render would otherwise be told its own
  // news again on every render.
  const onValidityRef = useRef(onValidityChange);
  useEffect(() => {
    onValidityRef.current = onValidityChange;
  }, [onValidityChange]);

  // The only client-side rule: a schema `int` that was filled in must be a
  // whole number. An empty field is an absent key, not an error.
  // A Map rather than an object: schema keys are the game's to choose, and
  // `constructor` or `__proto__` in a plain literal read back as something off
  // the prototype — an inherited function rendered into the field's error slot,
  // or a write that lands in the prototype setter and never blocks Save.
  const fieldErrors = useMemo(() => {
    const errs = new Map<string, string>();
    for (const f of schema) {
      if (f.type !== "int") continue;
      const s = fields[f.key] ?? "";
      if (s !== "" && !isWholeNumber(s)) {
        errs.set(f.key, "Must be a whole number.");
      }
    }
    return errs;
  }, [schema, fields]);

  const formValid = fieldErrors.size === 0;

  const rawParse = useMemo(() => {
    try {
      const parsed = JSON.parse(rawText) as unknown;
      if (
        parsed === null ||
        typeof parsed !== "object" ||
        Array.isArray(parsed)
      ) {
        return { error: "Metadata must be a JSON object." };
      }
      return { value: parsed as Record<string, unknown> };
    } catch (e) {
      return { error: (e as Error).message };
    }
  }, [rawText]);

  // Reported as valid until the operator touches the card. A guild whose
  // stored value already disagrees with its schema (§4d) must stay renameable
  // from this page — the card is the only place that can repair it, and it
  // cannot be if it locks Save the moment it loads.
  const valid = (mode === "form" ? formValid : !rawParse.error) || !touched;

  useEffect(() => {
    onValidityRef.current(valid);
  }, [valid]);

  // What the FORM would send right now — the object the per-field modified
  // marks are measured against, and the one Raw mode is seeded from.
  const current = useMemo(
    () => buildObject(schema, fields, extras, seed),
    [schema, fields, extras, seed],
  );

  // Modified marks — the header dot and the per-field ones below — appear only
  // once the operator has actually touched the card. A guild whose stored value
  // the form cannot represent exactly (a nested object under a `string` key,
  // §4d) already differs from `value` on the first render, and a dot on an
  // untouched field is a lie about who changed it. In Raw mode what the card
  // holds is the parsed textarea, not the form state left behind by the switch.
  const held =
    mode === "raw" && !rawParse.error && rawParse.value ? rawParse.value : current;
  const dirty = touched && !metadataEqual(held, value);

  /**
   * Schema fields whose stored value is not of the type the schema declares —
   * what a schema narrowed after the fact leaves behind. The value is shown as
   * it is, with the mismatch spelled out, because a form that silently blanks
   * data somebody else wrote is the worse of the two answers.
   */
  const mismatches = useMemo(
    () =>
      schema
        .filter((f) => {
          if (!Object.hasOwn(value, f.key)) return false;
          const kind = valueKind(value[f.key]);
          if (f.type === "int") return kind !== "int";
          if (f.type === "bool") return kind !== "bool";
          return kind !== "string";
        })
        .map((f) => ({
          key: f.key,
          declared: f.type,
          rendered: compactJson(value[f.key]),
        })),
    [schema, value],
  );

  function emit(
    nextFields: Record<string, string>,
    nextExtras: ExtraEntry[],
    nextSeed: Seed = seed,
  ) {
    setFields(nextFields);
    setExtras(nextExtras);
    setSeed(nextSeed);
    setTouched(true);
    // Emit from the freshly-computed state, not the stale closure.
    onChange(buildObject(schema, nextFields, nextExtras, nextSeed));
  }

  function setField(key: string, text: string) {
    emit({ ...fields, [key]: text }, extras);
  }

  function toggleField(key: string) {
    emit({ ...fields, [key]: fields[key] === "true" ? "false" : "true" }, extras);
  }

  function setExtraText(key: string, text: string) {
    emit(
      fields,
      extras.map((e) => (e.key === key ? { ...e, text } : e)),
    );
  }

  function toggleExtra(key: string) {
    emit(
      fields,
      extras.map((e) =>
        e.key === key ? { ...e, text: e.text === "true" ? "false" : "true" } : e,
      ),
    );
  }

  function removeExtra(key: string) {
    emit(
      fields,
      extras.filter((e) => e.key !== key),
    );
  }

  function handleRawChange(text: string) {
    setRawText(text);
    setTouched(true);
    try {
      const parsed = JSON.parse(text) as unknown;
      if (
        parsed !== null &&
        typeof parsed === "object" &&
        !Array.isArray(parsed)
      ) {
        onChange(parsed as Record<string, unknown>);
      }
    } catch {
      // Invalid JSON — don't emit; the validity effect surfaces the error.
    }
  }

  function switchMode(next: Mode) {
    if (next === mode) return;
    if (next === "raw") {
      // Seed the textarea from the form's own object so the two agree, keys
      // outside the schema included.
      setRawText(prettyJson(current));
    } else if (!rawParse.error && rawParse.value) {
      // Re-hydrate the form from a cleanly parsing raw object; a key added in
      // Raw JSON arrives here as a new "outside the schema" row.
      const parsed = rawParse.value;
      // The form can hold less than raw JSON can say — a nested object under a
      // schema key becomes text here. Emitting on the switch keeps the parent's
      // copy equal to what this card would actually save.
      emit(
        readFields(schema, parsed),
        readExtras(schema, parsed),
        readSeed(schema, parsed),
      );
    } else {
      // The raw JSON does not parse. The form keeps the values it had before
      // the switch, and the parent is told about them — without this it would
      // still hold the last object Raw mode emitted, and Save would send
      // something the card is no longer showing anywhere.
      emit(fields, extras);
    }
    setMode(next);
  }

  const fieldCount = `${schema.length} ${
    schema.length === 1 ? "field" : "fields"
  } from schema`;
  const extraCount = `${extras.length} extra ${
    extras.length === 1 ? "key" : "keys"
  }`;

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <h3 className={styles.cardTitle}>
            Metadata
            {dirty && (
              <span className={styles.modDot} title="modified" aria-hidden />
            )}
          </h3>
          <span className={styles.cardSub}>
            {hasSchema && mode === "form"
              ? `guilds.metadata · ${fieldCount}${
                  extras.length > 0 ? ` · ${extraCount}` : ""
                }`
              : "guilds.metadata · raw JSONB"}
          </span>
        </div>
        {hasSchema && (
          <div
            className={styles.segmented}
            role="group"
            aria-label="Metadata edit mode"
          >
            <button
              type="button"
              className={`${styles.seg} ${mode === "form" ? styles.segActive : ""}`}
              aria-pressed={mode === "form"}
              onClick={() => switchMode("form")}
            >
              Form
            </button>
            <button
              type="button"
              className={`${styles.seg} ${mode === "raw" ? styles.segActive : ""}`}
              aria-pressed={mode === "raw"}
              onClick={() => switchMode("raw")}
            >
              Raw JSON
            </button>
          </div>
        )}
      </div>

      <div className={styles.cardBody}>
        {mode === "form" && hasSchema && (
          <>
            {mismatches.length > 0 && (
              <div className={`${styles.alert} ${styles.alertWarn}`}>
                <AlertTriangle size={14} aria-hidden className={styles.alertIcon} />
                <div>
                  {mismatches.map((m) => (
                    <div key={m.key}>
                      <code className={styles.code}>{m.key}</code> is stored as{" "}
                      <code className={styles.code}>{m.rendered}</code>, and the
                      schema declares{" "}
                      <code className={styles.code}>{m.declared}</code>.
                    </div>
                  ))}
                  <div>
                    Shown as it is rather than blanked. Saving from the form
                    writes the schema&apos;s type, so correct the value here — or
                    edit it in Raw JSON to keep it exactly as it is.
                  </div>
                </div>
              </div>
            )}

            <div className={styles.metaGrid}>
              {drawingOrder(schema).map((field) => (
                <SchemaFieldRow
                  key={field.key}
                  idPrefix={idPrefix}
                  field={field}
                  isRequirement={
                    requirementKey !== "" && field.key === requirementKey
                  }
                  text={fields[field.key] ?? ""}
                  error={fieldErrors.get(field.key)}
                  modified={
                    touched && !sameJsonValue(current[field.key], value[field.key])
                  }
                  onText={(v) => setField(field.key, v)}
                  onToggle={() => toggleField(field.key)}
                />
              ))}
            </div>

            {extras.length > 0 && (
              <div className={styles.outside}>
                <div className={styles.outsideHead}>
                  <div>
                    <h4>
                      Outside the schema{" "}
                      <span className={styles.outsideCount}>
                        {extras.length} {extras.length === 1 ? "key" : "keys"}
                      </span>
                    </h4>
                    <p>
                      The game writes these itself and{" "}
                      <code className={styles.code}>guild.metadata_schema</code>{" "}
                      knows nothing about them. Edit or remove them here; saving
                      never drops them silently.
                    </p>
                  </div>
                </div>

                <div className={styles.okList}>
                  {extras.map((entry) => (
                    <ExtraRow
                      key={entry.key}
                      idPrefix={idPrefix}
                      entry={entry}
                      modified={
                        touched && !sameJsonValue(extraValue(entry), value[entry.key])
                      }
                      onText={(v) => setExtraText(entry.key, v)}
                      onToggle={() => toggleExtra(entry.key)}
                      onRemove={() => removeExtra(entry.key)}
                    />
                  ))}
                </div>
              </div>
            )}

            {Object.keys(value).length === 0 &&
              Object.keys(current).length === 0 && (
              <div className={`${styles.helper} ${styles.helperLoose}`}>
                This guild has written nothing yet:{" "}
                <code className={styles.code}>metadata</code> is an empty object.
                There is no “Outside the schema” section — there would be nothing
                in it.
              </div>
            )}
          </>
        )}

        {mode === "raw" && (
          <>
            <div className={`${styles.alert} ${styles.alertInfo}`}>
              <Info size={14} aria-hidden className={styles.alertIcon} />
              {hasSchema ? (
                <div>
                  Raw mode edits the whole{" "}
                  <code className={styles.code}>metadata</code> object, keys
                  outside the schema included. The server accepts any JSON
                  object:{" "}
                  <code className={styles.code}>guild.metadata_schema</code> is a
                  hint for the form, not a restriction, and the game writes what
                  it likes here through{" "}
                  <code className={styles.code}>PATCH /v1/guilds/:id</code>.
                </div>
              ) : (
                <div>
                  No <code className={styles.code}>guild.metadata_schema</code>{" "}
                  defined for this project — metadata edits as raw JSON only. Add
                  a schema in <b>Config → Guild</b> to get a typed form.
                </div>
              )}
            </div>
            <textarea
              className={`${styles.jsonEditor} ${
                rawParse.error ? styles.invalid : ""
              }`}
              aria-label="Raw JSON"
              spellCheck={false}
              value={rawText}
              aria-invalid={rawParse.error ? true : undefined}
              onChange={(e) => handleRawChange(e.target.value)}
            />
            {rawParse.error ? (
              <div className={styles.fieldError}>
                <AlertCircle size={12} aria-hidden className={styles.errIcon} />
                {rawParse.error}
              </div>
            ) : (
              <div className={styles.helper}>
                Must be a valid JSON object. Invalid JSON blocks Save.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// === One schema field ================================================= //

interface SchemaFieldRowProps {
  idPrefix: string;
  field: SchemaField;
  isRequirement: boolean;
  text: string;
  error?: string;
  modified: boolean;
  onText: (v: string) => void;
  onToggle: () => void;
}

function SchemaFieldRow({
  idPrefix,
  field,
  isRequirement,
  text,
  error,
  modified,
  onText,
  onToggle,
}: SchemaFieldRowProps) {
  const fieldId = `${idPrefix}-${field.key}`;
  const errId = `${fieldId}-err`;
  const on = text === "true";

  return (
    <div
      className={`${styles.metaField} ${
        field.type === "string" ? styles.span2 : ""
      }`}
    >
      <label htmlFor={fieldId}>
        <span className={styles.sk}>{field.key}</span>
        <span className={styles.typeTag}>{field.type}</span>
        {isRequirement && <span className={styles.reqTag}>requirement</span>}
        {modified && (
          <span className={styles.modDot} title="modified" aria-hidden />
        )}
      </label>

      {field.type === "bool" ? (
        <div className={styles.boolRow}>
          <button
            type="button"
            id={fieldId}
            role="switch"
            aria-checked={on}
            aria-label={field.key}
            className={`${styles.toggle} ${on ? styles.toggleOn : ""}`}
            onClick={onToggle}
          >
            <span className={styles.knob} />
          </button>
          <span className={`${styles.boolVal} ${on ? styles.boolValOn : ""}`}>
            {on ? "true" : "false"}
          </span>
        </div>
      ) : (
        <input
          id={fieldId}
          type="text"
          className={`${styles.input} ${
            field.type === "int" ? styles.mono : ""
          } ${error ? styles.invalid : ""} ${
            !error && modified ? styles.modified : ""
          }`}
          value={text}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errId : undefined}
          onChange={(e) => onText(e.target.value)}
        />
      )}

      {error && (
        <div className={styles.fieldError} id={errId}>
          <AlertCircle size={12} aria-hidden className={styles.errIcon} />
          {error}
        </div>
      )}

      {isRequirement && !error && (
        <div className={styles.helper}>
          The gate from <code className={styles.code}>guild.recommendations</code>{" "}
          — players who fall below this value never see the guild recommended.
        </div>
      )}
    </div>
  );
}

// === One key outside the schema ======================================= //

interface ExtraRowProps {
  idPrefix: string;
  entry: ExtraEntry;
  modified: boolean;
  onText: (v: string) => void;
  onToggle: () => void;
  onRemove: () => void;
}

function ExtraRow({
  idPrefix,
  entry,
  modified,
  onText,
  onToggle,
  onRemove,
}: ExtraRowProps) {
  const fieldId = `${idPrefix}-extra-${entry.key}`;
  const on = entry.text === "true";
  const scalar = isEditableScalar(entry.raw);

  return (
    <div className={`${styles.okRow} ${modified ? styles.okRowModified : ""}`}>
      <div className={styles.okKey}>
        <span className={styles.sk}>{entry.key}</span>
        <span className={styles.typeTag}>{entry.kind}</span>
        {modified && (
          <span className={styles.modDot} title="modified" aria-hidden />
        )}
      </div>

      <div className={styles.okCtl}>
        {!scalar ? (
          <>
            <div className={styles.okJson}>{compactJson(entry.raw)}</div>
            <div className={styles.okNote}>
              Not a scalar — edit it in Raw JSON. It can only be removed here.
            </div>
          </>
        ) : entry.kind === "bool" ? (
          <div className={styles.boolRow}>
            <button
              type="button"
              id={fieldId}
              role="switch"
              aria-checked={on}
              aria-label={entry.key}
              className={`${styles.toggle} ${on ? styles.toggleOn : ""}`}
              onClick={onToggle}
            >
              <span className={styles.knob} />
            </button>
            <span className={`${styles.boolVal} ${on ? styles.boolValOn : ""}`}>
              {on ? "true" : "false"}
            </span>
          </div>
        ) : (
          <input
            id={fieldId}
            type="text"
            aria-label={entry.key}
            className={`${styles.input} ${
              entry.kind === "string" ? "" : styles.mono
            } ${modified ? styles.modified : ""}`}
            value={entry.text}
            onChange={(e) => onText(e.target.value)}
          />
        )}
      </div>

      <button
        type="button"
        className={styles.iconBtn}
        aria-label={`Remove ${entry.key}`}
        onClick={onRemove}
      >
        <Trash2 size={14} aria-hidden />
      </button>
    </div>
  );
}
